import { useEffect, useSyncExternalStore } from "react";
import { getPrivateSessionState, subscribePrivateSession, establishPrivateSessionBinding } from "@/lib/api/private-session";
import { useQueryClient, type QueryFunctionContext } from "@tanstack/react-query";
import { acceptInspectedRestaurantSession } from "@/shared/sessionChannel";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/shared/queries";
import type { SellerSession } from "@/lib/api/types";
import { readSession, takeSessionCredentialRefresh } from "./readSession";

/**
 * The staff session.
 *
 * Reuses the seller session endpoint rather than minting a restaurant-specific
 * one: restaurant staff ARE seller accounts on a store, and a second session
 * surface would be a second place for a session bug to live.
 *
 * `retry: false` because a 401 here is an answer, not a blip — retrying it just
 * delays the login redirect on a device that has been logged out.
 *
 * The options are exported as well as hooked, because the login screen has to
 * READ the session before it can decide where to send this member (see
 * `landing.ts`) and that read must be the same query — same key, same cache
 * entry — the guarded shells then use, not a second fetch beside it.
 */
export const sessionQueryOptions = {
  queryKey: queryKeys.session,
  queryFn: async ({ client, signal }: QueryFunctionContext) => {
    const generation = getPrivateSessionState().generation;
    const session = await readSession(client, signal);
    establishPrivateSessionBinding(generation, session.seller.storeSlug, session.seller.id);
    return session;
  },
  retry: false,
  staleTime: 60_000,
  // Firebase SDK refreshes ID tokens; access revisions/focus re-inspect REST.
} as const;

export function useSession() {
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  const client = useQueryClient();
  const query = useQuery({ ...sessionQueryOptions, enabled: !session.changing,
    refetchOnWindowFocus: "always",
    queryFn: async ({ signal }) => {
      const generation = getPrivateSessionState().generation;
      const payload = await readSession(client, signal);
      await acceptInspectedRestaurantSession(client, payload, generation);
      if (getPrivateSessionState().generation === generation) establishPrivateSessionBinding(generation, payload.seller.storeSlug, payload.seller.id);
      return payload;
    },
  });
  const { data, refetch } = query;
  useEffect(() => {
    if (!session.changing && data && takeSessionCredentialRefresh(client)) void refetch({ cancelRefetch: false });
  }, [client, data, refetch, session.changing]);
  return { ...query, data: session.changing ? undefined : query.data,
    isLoading: session.changing || query.isLoading, error: session.changing ? null : query.error };

}

export function hasPermission(
  session: SellerSession | undefined,
  permission: string,
): boolean {
  return session?.seller.permissions.includes(permission) ?? false;
}

/** The permissions this app gates on. Mirrors the backend enum. */
export const PERMISSIONS = {
  POS_USE: "RESTAURANT_POS_USE",
  INVOICE_OPEN: "RESTAURANT_INVOICE_OPEN",
  INVOICE_APPEND: "RESTAURANT_INVOICE_APPEND",
  INVOICE_REMAKE: "RESTAURANT_INVOICE_REMAKE",
  /** Send the basket to the kitchen: DRAFT -> QUEUED (D18). */
  ITEM_FIRE: "RESTAURANT_ITEM_FIRE",
  INVOICE_VOID: "RESTAURANT_INVOICE_VOID",
  INVOICE_CLOSE: "RESTAURANT_INVOICE_CLOSE",
  INVOICE_SETTLE: "RESTAURANT_INVOICE_SETTLE",
  /* Earlier business days' bills (2026-11-02). Reading a PAID or CANCELLED
     bill from before today, and the history list beyond today, needs
     HISTORY_VIEW; any command on a bill from an earlier day needs PAST_EDIT.
     A bill still waiting to be paid stays readable at the till. */
  INVOICE_HISTORY_VIEW: "RESTAURANT_INVOICE_HISTORY_VIEW",
  INVOICE_PAST_EDIT: "RESTAURANT_INVOICE_PAST_EDIT",
  /* The acts CLOSE and VOID used to carry, split 2026-11-02. CLOSE keeps
     closing, the check and the captain; VOID keeps voiding a plate. */
  INVOICE_CANCEL: "RESTAURANT_INVOICE_CANCEL",
  INVOICE_REOPEN: "RESTAURANT_INVOICE_REOPEN",
  INVOICE_CHARGES_EDIT: "RESTAURANT_INVOICE_CHARGES_EDIT",
  INVOICE_COMP: "RESTAURANT_INVOICE_COMP",
  INVOICE_DISCOUNT: "RESTAURANT_INVOICE_DISCOUNT",
  INVOICE_PAYMENT_CORRECT: "RESTAURANT_INVOICE_PAYMENT_CORRECT",
  INVOICE_WRITE_OFF: "RESTAURANT_INVOICE_WRITE_OFF",
  /*
   * The kitchen (D18/D21). Four permissions, and they deliberately do NOT
   * travel together — `SYSTEM_ROLE_CATALOG` hands them out in three different
   * combinations, so a screen that gated on one of them for all four actions
   * would be wrong for two of the three roles:
   *
   * - KITCHEN_VIEW is the board itself, and it is kitchen-only. A waiter marks
   *   a plate served from the invoice screen; that is an ACTION, not a reason
   *   to hand them the whole kitchen.
   * - ITEM_PREPARE covers start AND ready — both are "the kitchen is working on
   *   it", and no role holds one without the other.
   * - ITEM_SERVE is separate and the KITCHEN role does NOT hold it: the runner
   *   who puts the plate on the table is the one who knows it got there. So the
   *   board's "served" action is gated apart from its start/ready pair.
   * - KITCHEN_PRINT is held by everyone (waiter, cashier and cook), because a
   *   lost chit is reprinted by whoever is standing nearest the printer.
   */
  KITCHEN_VIEW: "RESTAURANT_KITCHEN_VIEW",
  KITCHEN_PRINT: "RESTAURANT_KITCHEN_PRINT",
  ITEM_PREPARE: "RESTAURANT_ITEM_PREPARE",
  ITEM_SERVE: "RESTAURANT_ITEM_SERVE",
  // READ, not manage — and they do NOT travel together on the backend:
  // `GET /restaurant/kitchen-stations` and `GET /restaurant/menu-items` are
  // `RESTAURANT_MENU_VIEW` (the POS item picker needs it, and so does any
  // back-office screen that wants to name the items behind a count), while
  // every write is `RESTAURANT_MENU_MANAGE`. The stations screen needs VIEW to
  // list and `SETTINGS_MANAGE` to change anything, so it asks separately.
  MENU_VIEW: "RESTAURANT_MENU_VIEW",
  MENU_MANAGE: "RESTAURANT_MENU_MANAGE",
  // 86'ing (D32): mark a dish unavailable for the rest of service and put it
  // back. Held by the CASHIER preset and NOT by MENU_MANAGE holders by
  // implication — the toggle's endpoint accepts either, so every gate on THE
  // TOGGLE is `MENU_MANAGE || MENU_AVAILABILITY`.
  //
  // **It does not gate a SCREEN** (B117/E1). D50 put it on KITCHEN as well, so
  // "may 86 a dish" stopped implying "works the back office": the back-office
  // menu row was gated on it and a cook was landed on a page headed "the items
  // that are sold, and their cost". The cook's own 86 screen is
  // `/kitchen/availability`. A screen is gated on what its own endpoints
  // require, which for the back-office menu is `MENU_VIEW`.
  MENU_AVAILABILITY: "RESTAURANT_MENU_AVAILABILITY",
  FLOOR_MANAGE: "RESTAURANT_FLOOR_MANAGE",
  COSTING_MANAGE: "RESTAURANT_COSTING_MANAGE",
  // Receiving a delivery — استلام بضاعة (D147). Its own grant, in the OWNER,
  // MANAGER and CASHIER presets: the person who weighed the delivery records it
  // through cost-free reads. It opens `/manage/deliveries/new` and nothing that
  // shows an average, a recipe or "You owe".
  DELIVERY_RECORD: "RESTAURANT_DELIVERY_RECORD",
  // The spent-vs-gained report. Deliberately its OWN permission, not folded
  // into `COSTING_MANAGE` or `SETTINGS_MANAGE`: margins are the most sensitive
  // number in the store, and no system role below OWNER carries it (see the
  // controller's header comment).
  REPORT_VIEW: "RESTAURANT_REPORT_VIEW",
  /* The SUPERVISION reports — staff exceptions, dayparts, the closed drawers,
     the Z. Split off REPORT_VIEW so a floor manager can be given the tool that
     polices their own staff without the recipe margins riding along. Every
     route it opens takes EITHER grant, so a screen gated on it must check for
     both — see `canSeeOperationsReports`. */
  REPORT_OPERATIONS_VIEW: "RESTAURANT_REPORT_OPERATIONS_VIEW",
  // The cash-drawer shift feature (D28). Open shift, record movements, read
  // the live summary, close shift. NOT granted to any preset role below
  // OWNER — preset grants can never be un-granted (boot sync only adds), so
  // handing every existing cashier drawer authority would be irreversible.
  // An owner grants it via a custom role at the same moment they flip
  // `shiftTrackingEnabled` in settings.
  SHIFT_MANAGE: "RESTAURANT_SHIFT_MANAGE",
  // Per-line price override at the till (D46, S9). Like `SHIFT_MANAGE` above,
  // in NO preset role — a silent per-plate discount channel is one an owner
  // hands out a person at a time through a custom role, never by default.
  PRICE_OVERRIDE: "RESTAURANT_INVOICE_PRICE_OVERRIDE",
  // Refunding a settled bill (R9). Same rung as `SHIFT_MANAGE` and
  // `PRICE_OVERRIDE` above: in NO preset role below OWNER, because giving money
  // back is the most direct theft path at a till — the drawer still balances
  // afterwards — so an owner hands it out a person at a time through a custom
  // role. Nothing gates on it yet; the refund command arrives later.
  INVOICE_REFUND: "RESTAURANT_INVOICE_REFUND",
  // D156 — approve another session's void, comp, discount at the bill or
  // refund-to-stock with one's OWN password. A session holding it acts
  // directly; one without it opens the «اعتماد المدير» sheet while the store's
  // switch is on. OWNER and the restaurant MANAGER preset; never the cashier.
  EXCEPTION_APPROVE: "RESTAURANT_EXCEPTION_APPROVE",
  /**
   * Open ingredient substitution at the till (D61/D63).
   *
   * In no preset below OWNER, like PRICE_OVERRIDE above it — so the swap
   * button is HIDDEN rather than offered and then 403'd, and the pool read is
   * never attempted by a till that could not apply the result anyway.
   */
  INVOICE_SUBSTITUTE: "RESTAURANT_INVOICE_SUBSTITUTE",
  TIER_MANAGE: "RESTAURANT_TIER_MANAGE",
  SETTINGS_MANAGE: "RESTAURANT_SETTINGS_MANAGE",
  // Staff & roles reuse the seller RBAC permissions rather than minting
  // restaurant-specific ones — the roles they manage are store roles. None of
  // these is held by WAITER or CASHIER, so `/manage/staff` is OWNER-only.
  MEMBER_VIEW: "MEMBER_VIEW",
  MEMBER_INVITE: "MEMBER_INVITE",
  MEMBER_REMOVE: "MEMBER_REMOVE",
  MEMBER_EDIT_ROLES: "MEMBER_EDIT_ROLES",
  ROLE_VIEW: "ROLE_VIEW",
  ROLE_CREATE: "ROLE_CREATE",
  ROLE_EDIT: "ROLE_EDIT",
  ROLE_DELETE: "ROLE_DELETE",
  // Categories and product photos are seller objects as well (D13, D2). These
  // do NOT travel with the `RESTAURANT_*` set and they do not travel together
  // with each other: on a restaurant store OWNER and MANAGER hold all of them,
  // FULFILLMENT and SUPPORT hold the two VIEW permissions alone, and WAITER and
  // CASHIER hold none. Gate on the one the endpoint being called requires.
  // How a diner pays by transfer (D44) is a SELLER payment option, so it is
  // gated on the seller permissions that own it rather than a new
  // RESTAURANT_* one — the same reasoning as MEMBER_* and CATEGORY_* above.
  // On a restaurant store OWNER and MANAGER hold both; WAITER, CASHIER and
  // KITCHEN hold neither, which is right: the address money is sent to is not
  // a floor-staff setting.
  PAYMENT_VIEW: "PAYMENT_VIEW",
  PAYMENT_EDIT: "PAYMENT_EDIT",
  CATEGORY_VIEW: "CATEGORY_VIEW",
  CATEGORY_CREATE: "CATEGORY_CREATE",
  CATEGORY_EDIT: "CATEGORY_EDIT",
  CATEGORY_DELETE: "CATEGORY_DELETE",
  PRODUCT_VIEW: "PRODUCT_VIEW",
  PRODUCT_EDIT: "PRODUCT_EDIT",
  // The store profile is a seller object too; the logo upload (Settings ->
  // General) PATCHes it, and the route wants this. Reading it needs none.
  STORE_EDIT: "STORE_EDIT",
  /*
   * Driver backend B1 (2026-10-17). `DRIVER_WORK` is the DRIVER preset's one
   * grant — the driver's own work, done in the Trioty driver app, never here:
   * a session holding it and nothing the till or the back office needs lands
   * on the "use the driver app" screen (`landing.ts`, `DriverAppLandingPage`).
   * `DRIVER_INVITE` (OWNER, MANAGER) opens the team screen's "Invite a driver",
   * beside `MEMBER_INVITE`. The other three gate nothing in this app yet.
   */
  DRIVER_WORK: "RESTAURANT_DRIVER_WORK",
  DISPATCH_MANAGE: "RESTAURANT_DISPATCH_MANAGE",
  DISPATCH_CASH_CONFIRM: "RESTAURANT_DISPATCH_CASH_CONFIRM",
  DRIVER_DOCUMENTS_VIEW: "RESTAURANT_DRIVER_DOCUMENTS_VIEW",
  DRIVER_INVITE: "RESTAURANT_DRIVER_INVITE",
  /*
   * The customer list (founder decision 2026-10-25). `CUSTOMER_VIEW` opens
   * `/manage/customers` and a customer's drawer — OWNER, MANAGER and CASHIER
   * presets. `CUSTOMER_BLOCK` blocks or unblocks a phone for future WEBSITE
   * requests (never the till) — OWNER and MANAGER only.
   */
  CUSTOMER_VIEW: "RESTAURANT_CUSTOMER_VIEW",
  CUSTOMER_BLOCK: "RESTAURANT_CUSTOMER_BLOCK",
} as const;

/**
 * May this session read the SUPERVISION reports — staff exceptions, dayparts,
 * the closed drawers, the Z, the invoice export?
 *
 * An either/or, mirroring the routes: `RESTAURANT_REPORT_OPERATIONS_VIEW` is
 * the grant a floor manager holds, and `RESTAURANT_REPORT_VIEW` — the owner's
 * commercial read — still opens them, so nobody who could see these before the
 * split lost anything. A helper rather than the expression repeated at six call
 * sites, because the sixth is where it gets written with the wrong `||`.
 */
export function canSeeOperationsReports(
  session: SellerSession | undefined,
): boolean {
  return (
    hasPermission(session, PERMISSIONS.REPORT_OPERATIONS_VIEW) ||
    hasPermission(session, PERMISSIONS.REPORT_VIEW)
  );
}
